// frontend/src/components/loading/skeletonParts.js
//
// The building blocks the page skeletons draw with: a line of text, a solid
// shape, and the mobile section bar that Settings and the admin dashboard both
// render below their tab-row breakpoint.
//
// WHY LINES CARRY A FONT SIZE, NOT A HEIGHT. MUI's `text` Skeleton is
// `height: auto` around a non-breaking space, so its box is exactly one line of
// whatever font size and line height it is given - the same line box the real
// Typography it stands in for occupies. Handing a line the page's own
// fontSize / lineHeight (responsive values included) is what keeps the swap
// from skeleton to page free of a vertical jump, without re-deriving a pixel
// height for every heading.
//
// Every shape pulses (`animation="pulse"`); the fill and the motion gates come
// from the surrounding SkeletonStatus, never from here.

import React from "react";
import { Box, Skeleton } from "@mui/material";

/**
 * One line of text.
 *
 * @param {object} props
 * @param {string|number|object} props.width the line's length (responsive object allowed)
 * @param {string|object} props.fontSize the font size of the text it stands for
 * @param {number|string} [props.lineHeight=1.5] that text's line height
 * @param {object} [props.sx] extra sx (margins), merged last
 */
export function SkeletonLine({ width, fontSize, lineHeight = 1.5, sx }) {
  return (
    <Skeleton
      variant="text"
      animation="pulse"
      sx={{ width, maxWidth: "100%", fontSize, lineHeight, ...sx }}
    />
  );
}

/**
 * A solid rounded shape - an icon, a button, an avatar, a chip.
 *
 * @param {object} props
 * @param {string|number|object} props.width
 * @param {string|number|object} props.height
 * @param {string|number|object} [props.radius=8] corner radius (px when a number)
 * @param {object} [props.sx] extra sx, merged last
 */
export function SkeletonBlock({ width, height, radius = 8, sx }) {
  return (
    <Skeleton
      variant="rounded"
      animation="pulse"
      sx={{ width, height, borderRadius: radius, flexShrink: 0, maxWidth: "100%", ...sx }}
    />
  );
}

/**
 * The mobile section bar: the white, rounded strip holding the hamburger button
 * and the active section's icon + name, which Settings and the admin dashboard
 * render in place of their tab row below a breakpoint. Same box as theirs
 * (mb 3, px 1, py 0.75, 12px radius, hairline border, soft shadow) so the page
 * lands on it without a shift.
 *
 * @param {object} props
 * @param {string} props.borderColor the page's own hairline colour
 * @param {object|function} [props.sx] where it shows (a responsive `display`,
 *        or a theme callback), merged last
 */
export function MenuBarSkeleton({ borderColor, sx }) {
  return (
    <Box
      sx={[
        {
          mb: 3,
          px: 1,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderRadius: "12px",
          border: `1px solid ${borderColor}`,
          bgcolor: "#fff",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
        },
        // An object or a theme callback (the admin breakpoint is a px number).
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {/* The 22px hamburger glyph inside an 8px-padded IconButton. */}
      <SkeletonBlock width={38} height={38} radius="10px" />
      <SkeletonBlock width={18} height={18} radius="5px" />
      <SkeletonLine width={96} fontSize="0.95rem" />
    </Box>
  );
}
